import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ExternalLink, Star, Lock, Check, ArrowRight, Info } from 'lucide-react';
import Shell from '../components/TopBarShell';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { useAppCatalogue, iconFor, GROUP_ORDER, LT } from '../contexts/AppCatalogueContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { localizedApp } from '../lib/appLocale';
import api from '../utils/api';

/* Full catalogue page · two surfaces:
 *
 *  - "Mijn apps" (default) shows what the signed-in user actually has
 *    access to right now. Same data as the mega-menu, but presented as
 *    a wide list with the short description visible, grouped by section.
 *  - "Catalogus" shows every active app in the company, including the
 *    ones the user can't open yet · each locked row has a "Request
 *    access" button that emails the admins. After sending, the button
 *    swaps to a disabled "Sent" state so the same person can't spam
 *    the queue.
 *
 *  Search box narrows whichever surface is active, and a "Pending
 *  requests" banner surfaces above the list when any are open so the
 *  user remembers what they've asked for. */
export default function AppsCatalogue() {
  const t = useT();
  const { locale } = useLocale();
  const { apps: accessibleApps, loading: myLoading, error: myError, refresh: refreshMine } = useAppCatalogue();
  const { favorites, toggle } = useFavorites();
  const [view, setView] = useState('mine');  // 'mine' | 'catalog'
  const [q, setQ] = useState('');

  // Catalogue data · lazy-loaded the first time the Catalogus tab opens.
  // Each row carries a has_access flag so we can mark locked apps and
  // wire the request-access button only where it's relevant.
  const [catalogApps, setCatalogApps] = useState(null);
  const [catalogErr, setCatalogErr] = useState(null);
  const loadCatalog = () => {
    setCatalogErr(null);
    api.allApps().then(setCatalogApps).catch((e) => { setCatalogErr(e); setCatalogApps([]); });
  };
  useEffect(() => {
    if (view === 'catalog' && catalogApps === null) loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Pending requests · used to swap the button to "Sent" and surface a
  // small "you have N open requests" hint near the top.
  const [pendingRequests, setPendingRequests] = useState([]);
  const pendingSlugs = useMemo(
    () => new Set((pendingRequests || []).map(r => r.app?.slug).filter(Boolean)),
    [pendingRequests],
  );
  const refreshRequests = () => {
    api.myAppAccessRequests().then(setPendingRequests).catch(() => setPendingRequests([]));
  };
  useEffect(() => { refreshRequests(); }, []);

  const requestAccess = async (slug) => {
    try {
      const req = await api.requestAppAccess(slug);
      setPendingRequests(arr => {
        // Replace if already there (server returns the existing one), append otherwise.
        const without = (arr || []).filter(r => r.app?.slug !== slug);
        return [...without, req];
      });
    } catch (e) {
      // Surface the error inline · the row knows how to render this.
      throw e;
    }
  };

  // Pick the list to render based on the active tab. Mine uses the
  // shared catalogue context (already filtered by access), catalog
  // uses the all=1 endpoint with has_access annotations · but adapt
  // them to the same shape so the list component is generic.
  const sourceApps = view === 'mine'
    ? accessibleApps.map(a => ({ ...a, has_access: true }))
    : (catalogApps || []).map(a => ({
        ...a,
        Icon: iconFor(a.icon_name),
        dot: a.dot_color,
        tint: a.tint_color,
        to: a.internal_route || null,
        href: a.href || null,
        internal: !!a.internal_route,
      }));

  const needle = q.trim().toLowerCase();
  const filtered = useMemo(
    () => sourceApps.filter(a => {
      if (!needle) return true;
      const { name, description, tagline } = localizedApp(a, locale);
      return name.toLowerCase().includes(needle)
        || (tagline || '').toLowerCase().includes(needle)
        || (description || '').toLowerCase().includes(needle)
        || (a.group || '').toLowerCase().includes(needle);
    }),
    [sourceApps, needle, locale],
  );

  const grouped = useMemo(() => {
    const g = Object.fromEntries(GROUP_ORDER.map(k => [k, []]));
    for (const a of filtered) (g[a.group] || g.other).push(a);
    return g;
  }, [filtered]);

  const isLoading = view === 'mine' ? myLoading : (catalogApps === null);
  const isError   = view === 'mine' ? myError   : catalogErr;
  const onRetry   = view === 'mine' ? refreshMine : loadCatalog;

  return (
    <Shell>
      <header className="mb-6 max-w-[1100px]">
        <h1 className="font-display text-[2rem] sm:text-[2.5rem] leading-tight tracking-[-0.02em]"
            style={{ color: LT.greenDeep }}>
          {t('apps.title')}
        </h1>
        <p className="mt-2 text-[15px]" style={{ color: LT.green50 }}>
          {view === 'mine' ? t('apps.lede.mine') : t('apps.lede.catalog')}
        </p>

        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-md overflow-hidden text-[13px]"
               style={{ border: `1px solid ${LT.green30}` }}>
            <TabBtn active={view === 'mine'}    onClick={() => setView('mine')}    label={t('apps.tab.mine')} />
            <TabBtn active={view === 'catalog'} onClick={() => setView('catalog')} label={t('apps.tab.catalog')} />
          </div>
          <div className="relative flex-1 min-w-[260px] max-w-[420px]">
            <Search className="h-3.5 w-3.5 absolute top-2.5 left-3" style={{ color: LT.green50 }} />
            <input
              type="search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={view === 'mine' ? t('apps.searchPh.mine') : t('apps.searchPh.catalog')}
              className="w-full rounded-md pl-8 pr-3 py-2 text-[13.5px] outline-none"
              style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, color: LT.greenDeep }}
            />
          </div>
        </div>

        {pendingRequests.length > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px]"
               style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, color: LT.green50 }}>
            <Info className="h-3.5 w-3.5" />
            {t('apps.pendingHint', { n: pendingRequests.length })}
          </div>
        )}
      </header>

      {GROUP_ORDER.map(key => grouped[key].length > 0 && (
        <section key={key} className="mt-8 max-w-[1100px]">
          <div className="flex items-baseline gap-3 mb-2.5">
            <h2 className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: LT.green50 }}>
              {t(`splash.group.${key}.eyebrow`)}
            </h2>
            <div className="flex-1 h-px" style={{ background: LT.green30 }} />
            <span className="text-[12px] font-num" style={{ color: LT.green50 }}>{grouped[key].length}</span>
          </div>
          <ul className="space-y-1">
            {grouped[key].map(app => (
              <AppRow
                key={app.slug}
                app={app}
                view={view}
                isFav={favorites.includes(app.slug)}
                onToggleFav={() => toggle(app.slug)}
                hasPendingRequest={pendingSlugs.has(app.slug)}
                onRequestAccess={requestAccess}
                t={t}
                locale={locale}
              />
            ))}
          </ul>
        </section>
      ))}

      {filtered.length === 0 && (
        <div className="mt-10 rounded-2xl p-6 text-[13px] max-w-[1100px]"
             style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
          {isLoading
            ? t('common.loading')
            : isError
              ? (
                <span>
                  {t('apps.loadFailed')}{' '}
                  <button type="button" onClick={onRetry}
                          className="underline" style={{ color: LT.greenDeep }}>
                    {t('common.retry')}
                  </button>
                </span>
              )
              : view === 'mine'
                ? t('apps.noneMine')
                : t('apps.noResults')}
        </div>
      )}
    </Shell>
  );
}

const TabBtn = ({ active, onClick, label }) => (
  <button type="button" onClick={onClick}
    className="px-3 py-1.5 transition"
    style={{
      background: active ? LT.greenDeep : 'transparent',
      color: active ? LT.beigeLight : LT.green50,
      fontWeight: active ? 500 : 400,
    }}>
    {label}
  </button>
);

/* Single-row entry · logo, name, inline tagline (truncates), actions
 * right-aligned. Reads almost like a table row · scales to lots of
 * apps without dominating the page. Locked rows in Catalogus mode
 * drop the Open link and surface a Request-access affordance instead. */
const AppRow = ({ app, view, isFav, onToggleFav, hasPendingRequest, onRequestAccess, t, locale }) => {
  const Icon = app.Icon;
  const { name, tagline } = localizedApp(app, locale);
  const hasAccess = view === 'mine' || app.has_access;
  const locked = !hasAccess;
  // Pour les boutons posés dans la ligne : la ligne entière est cliquable et
  // ouvre la fiche, donc on annule tout.
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  // Pour les liens « Ouvrir » : on veut seulement empêcher le clic de remonter
  // à la ligne. Un preventDefault ici annulerait la navigation du lien
  // lui-même — c'est ce qui rendait le bouton inerte.
  const stopBubble = (e) => { e.stopPropagation(); };
  const [requesting, setRequesting] = useState(false);
  const [requestErr, setRequestErr] = useState('');
  const requested = hasPendingRequest;

  const onRequest = async (e) => {
    stop(e);
    setRequesting(true);
    setRequestErr('');
    try {
      await onRequestAccess(app.slug);
    } catch (err) {
      setRequestErr(err.response?.data?.detail || err.message);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <li>
      <Link
        to={`/apps/${app.slug}`}
        title={tagline || name}
        className="flex items-center gap-3 px-3 py-1.5 rounded-md transition hover:bg-black/[0.025]"
        style={{
          background: locked
            ? LT.beigeLight
            : `linear-gradient(140deg, ${LT.beigeLight} 0%, ${app.tint || 'rgba(43,65,56,0.04)'} 100%)`,
          border: `1px solid ${LT.green30}`,
          opacity: locked ? 0.78 : 1,
        }}
      >
        <span className="h-7 w-7 rounded-md flex items-center justify-center shrink-0 overflow-hidden"
              style={{ background: LT.beigeLight, border: `1px solid ${(app.dot || '#888')}30` }}>
          {app.logo
            ? <img src={app.logo} alt="" className="h-full w-full object-contain p-1" />
            : Icon
              ? <Icon className="h-3.5 w-3.5" style={{ color: app.dot || LT.green50 }} />
              : <span className="h-1.5 w-1.5 rounded-full" style={{ background: app.dot || LT.green50 }} />}
        </span>

        <span className="font-display text-[14px] leading-tight truncate shrink-0 max-w-[40%]"
              style={{ color: LT.greenDeep }}>
          {name}
        </span>

        {tagline && (
          <span className="text-[12.5px] leading-tight truncate flex-1 min-w-0 hidden sm:inline"
                style={{ color: LT.green50 }}>
            <span className="opacity-60 mr-1.5">·</span>{tagline}
          </span>
        )}
        {!tagline && <span className="flex-1" />}

        {app.is_featured && (
          <span className="hidden md:inline-block text-[9.5px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full shrink-0"
                style={{ background: LT.yellowLight, color: LT.greenDeep }}>
            {t('apps.featured')}
          </span>
        )}
        {locked && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full shrink-0"
                style={{ background: 'transparent', color: LT.green50, border: `1px solid ${LT.green30}` }}>
            <Lock className="h-2.5 w-2.5" /> {t('apps.locked')}
          </span>
        )}

        <div className="flex items-center gap-1 shrink-0">
          {locked ? (
            requested ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11.5px]"
                    style={{ background: LT.beigeLight, color: LT.green50, border: `1px solid ${LT.green30}` }}>
                <Check className="h-3 w-3" /> {t('apps.requested')}
              </span>
            ) : (
              <button type="button" onClick={onRequest} disabled={requesting}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11.5px] transition hover:opacity-80"
                      style={{ background: LT.greenDeep, color: LT.beigeLight }}>
                {requesting ? t('apps.requesting') : t('apps.requestAccess')}
              </button>
            )
          ) : (app.internal && app.to) ? (
            <Link to={app.to} onClick={stopBubble}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] transition hover:opacity-80"
                  style={{ background: LT.greenDeep, color: LT.beigeLight, fontWeight: 500 }}>
              {t('apps.open')} <ArrowRight className="h-3 w-3" />
            </Link>
          ) : app.href ? (
            <a href={app.href} target="_blank" rel="noreferrer" onClick={stopBubble}
               className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] transition hover:opacity-80"
               style={{ background: LT.greenDeep, color: LT.beigeLight, fontWeight: 500 }}>
              {t('apps.open')} <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
          <button
            type="button"
            onClick={(e) => { stop(e); onToggleFav(); }}
            className={`p-1 rounded transition ${isFav ? 'opacity-100' : 'opacity-30 hover:opacity-90'}`}
            title={isFav ? t('splash.fav.unpin') : t('splash.fav.pin')}
            aria-label={isFav ? t('splash.fav.unpin') : t('splash.fav.pin')}
          >
            <Star className="h-3.5 w-3.5" style={{ color: LT.greenDeep, fill: isFav ? LT.yellowLight : 'transparent' }} />
          </button>
        </div>
      </Link>
      {requestErr && (
        <p className="px-3 mt-1 text-[11.5px]" style={{ color: '#b34b3b' }}>{requestErr}</p>
      )}
    </li>
  );
};
